import clsx from "clsx";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "./core";

export function Modal({ open, onClose, title, children, footer, width = "md", description }: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: "sm" | "md" | "lg" | "xl";
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Callers pass an inline onClose, so depending on it re-ran the effect on every keystroke in a field inside the
  // modal and moved focus back to the panel. Keep the latest callback in a ref and run the effect on open only.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close.current();
    document.addEventListener("keydown", onKey);
    // an autoFocus field inside has already taken focus; don't steal it
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  if (!open) return null;
  const widths = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-4xl", xl: "max-w-6xl" };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 p-4 pt-[8vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={panel} tabIndex={-1} role="dialog" aria-modal className={clsx("w-full rounded-md border border-line bg-surface shadow-pop outline-none", widths[width])}>
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-3">
          <div>
            <h2 className="text-base font-semibold">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-ink-3">{description}</p>}
          </div>
          <button className="rounded p-1 text-ink-3 hover:bg-subtle hover:text-ink" onClick={onClose} aria-label="Kapat">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 scroll-thin">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-line bg-subtle px-5 py-3">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({ open, onClose, title, children, footer, width = 560 }: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-40 bg-black/20" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside style={{ width }} className="absolute right-0 top-0 flex h-full max-w-full flex-col border-l border-line bg-surface shadow-pop">
        <header className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <h2 className="truncate text-sm font-semibold">{title}</h2>
          <button className="rounded p-1 text-ink-3 hover:bg-subtle hover:text-ink" onClick={onClose} aria-label="Kapat">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 scroll-thin">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-line bg-subtle px-4 py-3">{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ open, title, message, confirmLabel = "Onayla", danger, onConfirm, onClose, loading }: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Vazgeç</Button>
          <Button variant={danger ? "danger" : "primary"} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-ink-2">{message}</div>
    </Modal>
  );
}

// ------------------------------------------------------------------ toasts

interface Toast {
  id: number;
  tone: "ok" | "bad" | "brand";
  text: string;
}

const ToastContext = createContext<(tone: Toast["tone"], text: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((tone: Toast["tone"], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, tone, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === "bad" ? 7000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[60] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={clsx(
                "rounded border bg-surface px-3 py-2.5 text-sm shadow-pop",
                t.tone === "ok" && "border-ok/40 border-l-4 border-l-ok",
                t.tone === "bad" && "border-bad/40 border-l-4 border-l-bad",
                t.tone === "brand" && "border-brand/40 border-l-4 border-l-brand",
              )}
            >
              {t.text}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastContext);
  return {
    ok: (text: string) => push("ok", text),
    error: (text: string) => push("bad", text),
    info: (text: string) => push("brand", text),
  };
}
